import styled from '@emotion/styled'
import { ExtranetShopProductListItemDto } from '@pharmac-extranet/extranet-api'
import {
  ExtranetContainer,
  ExtranetListItem,
  ExtranetListItemProps,
  MediaQuery,
  formatDateString,
  htmlStringParser,
  useBreakpoint,
  useLanguage,
} from '@pharmac-extranet/shared'
import {
  PharmacBadge,
  PharmacButton,
  PharmacContainer,
  PharmacIcon,
  PharmacText,
  PharmacTooltip,
  PharmacTruncate,
} from '@pharmac/ui'
import { useNavigate } from 'react-router-dom'
import { useAuthenticationContext } from '../../features/authentication/useAuthentication'
import ImageNotAvailable from '../dose-dispensing-catalog/image-not-available/NoImageAvailable_16_9.jpg'
import { useShoppingCartContext } from '../navigation/shopping-cart/ShoppingCartContext'
import { useShop } from './useShop'
import { formatPriceDecimal } from './util/shop-utils'

type ProductProps = {
  product: ExtranetShopProductListItemDto
  hook: ReturnType<typeof useShoppingCartContext>
}

const TopBarText = styled(PharmacText)`
  width: max-content;

  ${MediaQuery.maxMobile} {
    padding: var(--spacing-tiny) 0;
  }
`

const Badge = styled(PharmacBadge)<{ isDesktop?: boolean }>`
  padding: ${(p) => (p.isDesktop ? '0 var(--spacing-small)' : undefined)};
  ${MediaQuery.maxMobile} {
    width: fit-content;
  }
`

const ContentText = styled(PharmacText)`
  max-height: 100%;
  overflow: hidden;
`

export const ShopOnSaleListItem = ({ product, hook }: ProductProps) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const {
    subscribeToFreeProduct,
    hasBuyerRole,
    hasBuyingBan,
    productCategory,
    langLabel,
    myRoles,
    formatTimecodeToFancyForm,
  } = useShop(product.productId)
  const { isPharmacCustomerService } = useAuthenticationContext()

  const { minDesktop, maxMobile } = useBreakpoint()

  const itemProps: ExtranetListItemProps = {
    isShop: true,
    border: true,
    image: product.image
      ? {
          desktop: product.image.desktopUrl,
          desktopLarge: product.image.desktopLargeUrl,
          mobile: product.image.mobileUrl,
        }
      : {
          desktop: ImageNotAvailable,
          desktopLarge: ImageNotAvailable,
          mobile: ImageNotAvailable,
        },
    mobileLabels: [
      <>
        {maxMobile && (
          <ExtranetContainer
            full
            alignCenter
            flexWrap
            marginLargeBottom
          >
            {product.language?.code && (
              <ExtranetContainer paddingMediumRight>
                <TopBarText>{langLabel(String(product.language?.code))}</TopBarText>
              </ExtranetContainer>
            )}
            <ExtranetContainer paddingMediumRight>
              <TopBarText>{productCategory(product?.productFamily)}</TopBarText>
            </ExtranetContainer>
            <ExtranetContainer paddingMediumRight>
              <TopBarText>
                {t('extranet:shop.published')}: {formatDateString(String(product.publishDate))}
              </TopBarText>
            </ExtranetContainer>
            {product.productFamily === 'recording-product' && product.videoDuration && (
              <ExtranetContainer alignCenter>
                <PharmacIcon
                  size="15px"
                  icon="clock"
                />
                <PharmacText
                  marginSmallLeft
                  paddingMediumRight
                >
                  {formatTimecodeToFancyForm(product.videoDuration)}
                </PharmacText>
              </ExtranetContainer>
            )}
          </ExtranetContainer>
        )}
      </>,
    ],
    title: PharmacTruncate(product.title, 75),
    labels: [
      <>
        {!maxMobile && (
          <ExtranetContainer
            full
            alignCenter
          >
            {product.language?.code && (
              <ExtranetContainer paddingMediumRight>
                <TopBarText>{langLabel(String(product.language?.code))}</TopBarText>
              </ExtranetContainer>
            )}
            <ExtranetContainer paddingMediumRight>
              <TopBarText>{productCategory(product?.productFamily)}</TopBarText>
            </ExtranetContainer>
            <ExtranetContainer paddingMediumRight>
              <TopBarText>
                {t('extranet:shop.published')}: {formatDateString(String(product.publishDate))}
              </TopBarText>
            </ExtranetContainer>
            {product.productFamily === 'recording-product' && product.videoDuration && (
              <ExtranetContainer alignCenter>
                <PharmacIcon
                  size="15px"
                  icon="clock"
                />
                <TopBarText
                  marginSmallLeft
                  paddingMediumRight
                >
                  {formatTimecodeToFancyForm(product.videoDuration)}
                </TopBarText>
              </ExtranetContainer>
            )}
            <PharmacContainer style={{ display: 'flex', overflow: 'hidden' }}>
              {product.tags?.map((a) => (
                <Badge
                  isDesktop
                  key={a.id}
                  small
                  variant="primary"
                  marginSmallRight
                >
                  {a.name}
                </Badge>
              ))}
            </PharmacContainer>
          </ExtranetContainer>
        )}
      </>,
    ],
    content: (
      <ContentText
        dangerouslySetInnerHTML={{
          __html: htmlStringParser(
            PharmacTruncate(product.description, minDesktop ? 460 : !maxMobile ? 200 : 100) ?? ''
          ),
        }}
      />
    ),
    mobileContent: (
      <>
        {maxMobile && (
          <>
            <ExtranetContainer
              alignCenter
              marginMediumTop
              third
              style={{ backgroundColor: '#F3E9F4', height: '35px', borderRadius: '25px' }}
            >
              <ExtranetContainer
                alignCenter
                justifyCenter
                style={{
                  backgroundColor: '#B87EBC80',
                  height: '100%',
                  width: '25%',
                  borderRadius: '25px',
                }}
              >
                <PharmacIcon
                  size="15px"
                  icon="euro"
                />
              </ExtranetContainer>
              <PharmacText
                weight="bold"
                paddingSmallLeft
              >
                {product.unitSellingPrice === 0
                  ? t('common:free')
                  : formatPriceDecimal(product.unitSellingPrice, 2)}
              </PharmacText>
            </ExtranetContainer>
            <ExtranetContainer paddingMediumTop>
              <PharmacContainer style={{ display: 'flex', overflow: 'hidden' }}>
                {product.tags?.map((a) => (
                  <Badge
                    isDesktop={false}
                    key={a.id}
                    small
                    variant="primary"
                    marginSmallRight
                  >
                    {a.name}
                  </Badge>
                ))}
              </PharmacContainer>
            </ExtranetContainer>
            <ExtranetContainer
              full
              marginMediumTop
              style={{ borderTop: '2px solid var(--color-accent-4)' }}
            >
              <ExtranetContainer
                full
                spaceBetween
                paddingSmallTop
              >
                <PharmacButton
                  size="small"
                  variant="secondary"
                  onClick={() => navigate(`${product.productId}`)}
                >
                  <PharmacText weight="semibold">
                    {t('extranet:shop.buttons.learnMore')}
                  </PharmacText>
                </PharmacButton>
                {product.unitSellingPrice !== 0 ||
                product.productFamily === 'service-product' ||
                product.productFamily === 'physical-product' ? (
                  <ExtranetContainer alignCenter>
                    {!myRoles?.isAdmin && !hasBuyerRole && !hasBuyingBan && (
                      <PharmacTooltip
                        paddingSmallLeft
                        backgroundColor="var(--color-white)"
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="20px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                      </PharmacTooltip>
                    )}
                    {hasBuyingBan && product.unitSellingPrice !== 0 && (
                      <PharmacTooltip
                        paddingSmallLeft
                        backgroundColor="var(--color-white)"
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="20px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                      </PharmacTooltip>
                    )}
                    <PharmacButton
                      size="small"
                      marginSmallLeft
                      onClick={() => hook.addAndRemoveCartButton(product, product.productId)}
                      disabled={
                        (!myRoles?.isAdmin && !hasBuyerRole && product.unitSellingPrice !== 0) ||
                        (hasBuyingBan && product.unitSellingPrice !== 0) ||
                        isPharmacCustomerService
                      }
                      variant={
                        (!myRoles?.isAdmin && !hasBuyerRole && product.unitSellingPrice !== 0) ||
                        (hasBuyingBan && product.unitSellingPrice !== 0) ||
                        isPharmacCustomerService
                          ? 'secondary'
                          : 'primary'
                      }
                    >
                      <PharmacIcon
                        size="13px"
                        icon="shoppingCart"
                      />
                      <PharmacText paddingSmallHorizontal>
                        {hook.selectedProducts &&
                        hook.selectedProducts[product.productId]?.includes(product.productId) ? (
                          <>{t('extranet:shop.buttons.removeFromCart')}</>
                        ) : (
                          <>{t('extranet:shop.buttons.addToCart')}</>
                        )}
                      </PharmacText>
                    </PharmacButton>
                  </ExtranetContainer>
                ) : (
                  <ExtranetContainer alignCenter>
                    {!product.canBeFreeSubscribed && (
                      <PharmacTooltip
                        paddingSmallLeft
                        backgroundColor="var(--color-white)"
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="20px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>{t('extranet:shop.noRequiredProfRolesTooltip')}</PharmacText>
                      </PharmacTooltip>
                    )}
                    <PharmacButton
                      size="small"
                      marginSmallLeft
                      onClick={() => subscribeToFreeProduct()}
                      disabled={!product.canBeFreeSubscribed}
                      variant={product.canBeFreeSubscribed ? 'primary' : 'secondary'}
                    >
                      <PharmacIcon
                        size="15px"
                        icon="plus"
                      />
                      <PharmacText paddingSmallHorizontal>
                        {t('extranet:shop.buttons.addToOwnProducts')}
                      </PharmacText>
                    </PharmacButton>
                  </ExtranetContainer>
                )}
              </ExtranetContainer>
            </ExtranetContainer>
          </>
        )}
      </>
    ),
    footerContent: (
      <>
        {!maxMobile && (
          <ExtranetContainer
            alignCenter
            full
            paddingMediumVertical={maxMobile}
            marginSmallTop={maxMobile}
            style={{
              borderTop: maxMobile ? '2px solid var(--color-accent-4)' : undefined,
              marginBottom: !maxMobile ? 'var(--spacing-tiny)' : undefined,
            }}
          >
            <ExtranetContainer
              third={!maxMobile}
              full={maxMobile}
            >
              <ExtranetContainer
                alignCenter
                half
                style={{ backgroundColor: '#F3E9F4', height: '30px', borderRadius: '25px' }}
              >
                <ExtranetContainer
                  alignCenter
                  justifyCenter
                  style={{
                    backgroundColor: '#B87EBC80',
                    height: '100%',
                    width: '25%',
                    borderRadius: '25px',
                  }}
                >
                  <PharmacIcon
                    size="15px"
                    icon="euro"
                  />
                </ExtranetContainer>
                <PharmacText
                  weight="bold"
                  paddingSmallLeft
                >
                  {product.unitSellingPrice === 0
                    ? t('common:free')
                    : formatPriceDecimal(product.unitSellingPrice, 2)}
                </PharmacText>
              </ExtranetContainer>
            </ExtranetContainer>
            <ExtranetContainer
              twoThirds={!maxMobile}
              full={maxMobile}
              justifyEnd={!maxMobile}
              spaceBetween={maxMobile}
            >
              <PharmacButton
                size="small"
                variant="secondary"
                onClick={() => navigate(`${product.productId}`)}
              >
                <PharmacText weight="semibold">{t('extranet:shop.buttons.learnMore')}</PharmacText>
              </PharmacButton>
              {product.unitSellingPrice !== 0 ||
              product.productFamily === 'service-product' ||
              product.productFamily === 'physical-product' ? (
                <ExtranetContainer alignCenter>
                  {!myRoles?.isAdmin &&
                    !hasBuyerRole &&
                    !hasBuyingBan &&
                    product.unitSellingPrice !== 0 && (
                      <PharmacTooltip
                        paddingSmallLeft
                        backgroundColor="var(--color-white)"
                        handle={
                          <PharmacIcon
                            style={{ color: 'var(--color-info)' }}
                            size="20px"
                            icon="info"
                          />
                        }
                      >
                        <PharmacText>{t('extranet:shop.noBuyerRoleTooltip')}</PharmacText>
                      </PharmacTooltip>
                    )}
                  {hasBuyingBan && product.unitSellingPrice !== 0 && (
                    <PharmacTooltip
                      paddingSmallLeft
                      backgroundColor="var(--color-white)"
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="20px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>{t('extranet:shop.purchaseRightDeniedTooltip')}</PharmacText>
                    </PharmacTooltip>
                  )}
                  <PharmacButton
                    size="small"
                    marginSmallLeft
                    onClick={() => hook.addAndRemoveCartButton(product, product.productId)}
                    disabled={
                      (!myRoles?.isAdmin && !hasBuyerRole && product.unitSellingPrice !== 0) ||
                      (hasBuyingBan && product.unitSellingPrice !== 0) ||
                      isPharmacCustomerService
                    }
                    variant={
                      (!myRoles?.isAdmin && !hasBuyerRole && product.unitSellingPrice !== 0) ||
                      (hasBuyingBan && product.unitSellingPrice !== 0) ||
                      isPharmacCustomerService
                        ? 'secondary'
                        : 'primary'
                    }
                  >
                    <PharmacIcon
                      size="15px"
                      icon="shoppingCart"
                    />
                    <PharmacText paddingSmallHorizontal>
                      {hook.selectedProducts &&
                      hook.selectedProducts[product.productId]?.includes(product.productId) ? (
                        <>{t('extranet:shop.buttons.removeFromCart')}</>
                      ) : (
                        <>{t('extranet:shop.buttons.addToCart')}</>
                      )}
                    </PharmacText>
                  </PharmacButton>
                </ExtranetContainer>
              ) : (
                <ExtranetContainer alignCenter>
                  {!product.canBeFreeSubscribed && (
                    <PharmacTooltip
                      paddingSmallLeft
                      backgroundColor="var(--color-white)"
                      handle={
                        <PharmacIcon
                          style={{ color: 'var(--color-info)' }}
                          size="20px"
                          icon="info"
                        />
                      }
                    >
                      <PharmacText>{t('extranet:shop.noRequiredProfRolesTooltip')}</PharmacText>
                    </PharmacTooltip>
                  )}
                  <PharmacButton
                    size="small"
                    marginSmallLeft
                    onClick={() => subscribeToFreeProduct()}
                    disabled={!product.canBeFreeSubscribed || isPharmacCustomerService}
                    variant={
                      !product.canBeFreeSubscribed || isPharmacCustomerService
                        ? 'secondary'
                        : 'primary'
                    }
                  >
                    <PharmacIcon
                      size="15px"
                      icon="plus"
                    />
                    <PharmacText paddingSmallHorizontal>
                      {t('extranet:shop.buttons.addToOwnProducts')}
                    </PharmacText>
                  </PharmacButton>
                </ExtranetContainer>
              )}
            </ExtranetContainer>
          </ExtranetContainer>
        )}
      </>
    ),
    navigateFn: () => navigate(`${product.productId}`),
    ariaDescription: 'Link to product',
  }

  return (
    <ExtranetListItem
      hasMobileLabels
      key={'product-' + product.productId}
      {...itemProps}
    />
  )
}
